import React from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { modeLabel } from "../helper/repeatabilityText";
import { rgbString } from "../helper/colorConversion";
import PaletteSampleTooltip from "./PaletteSampleTooltip";

/** Accent per category, keyed as `directionKey` keys them: the contrast
 *  directions by the mode in which the samples are close, then "both" and
 *  "stable" (shared with the pair plot's shaded areas). */
export const DIRECTION_COLORS = { 1: "#4C7EFF", 2: "#ea580c", both: "#059669", stable: "#7c3aed" };

const lowerMode = (mode) => modeLabel(mode).toLowerCase();

/** The category's kind; entries without one are the contrast directions. */
export const directionKind = (direction) => direction.kind || "contrast";

/** "1" / "2" for the contrast directions (the close mode), else the kind. */
export function directionKey(direction) {
    return directionKind(direction) === "contrast" ? String(direction.close_mode) : direction.kind;
}

/** "Close in mode 1, far in mode 2", "Close in both modes", "Stable across modes" */
export function directionLabel(direction) {
    const kind = directionKind(direction);
    if (kind === "both") return "Close in both modes";
    if (kind === "stable") return "Stable across modes";
    return `Close in ${lowerMode(direction.close_mode)}, far in ${lowerMode(direction.far_mode)}`;
}

const dE = (value) => (typeof value === "number" ? value.toFixed(1) : value);

/** The category's limits as the overview states them. */
export function directionLimitsText(direction) {
    const kind = directionKind(direction);
    if (kind === "both") return `max ΔE00 ${direction.max_mode_1} in mode 1 · ${direction.max_mode_2} in mode 2`;
    if (kind === "stable") return `max ΔE00 ${direction.max_between} between modes`;
    return `max ΔE00 ${direction.max_close} · min ΔE00 ${direction.min_far}`;
}

/** What a category selected, as the pages state it: pairs (and the samples
 *  they hold) for a pair category, samples for the stable category.
 *  `pairs`/`samples` default to the live result's `pairs`/`selected`; the
 *  overview's summary passes its own counts. */
export function selectedText(direction, pairs = direction.pairs, samples = direction.selected) {
    if (directionKind(direction) === "stable") return `${samples} sample${samples === 1 ? "" : "s"}`;
    return `${pairs} pair${pairs === 1 ? "" : "s"} · ${samples} samples`;
}

/** A member in one mode. The hover card is the palette's (design, marking
 *  time) when `entry` is given, with the color of this mode's reading. */
function Swatch({ member, mode, entry }) {
    const reading = member.modes[String(mode)];
    const [l, a, b] = reading.lab;
    const color = rgbString(reading.rgb);
    const tooltipEntry = {
        ...(entry || { sample_id: member.sample_id, width_mm: member.width_mm }),
        hex: color, l_star: l, a_star: a, b_star: b,
    };
    return (
        <Tooltip title={<PaletteSampleTooltip entry={tooltipEntry} note={modeLabel(mode)} />} placement="top">
            <Box
                data-testid="mode-contrast-swatch"
                sx={{
                    width: 30, height: 30, borderRadius: 0.75,
                    bgcolor: color, border: "1px solid rgba(15, 23, 42, 0.2)",
                }}
            />
        </Tooltip>
    );
}

/** What a pair keeps, per kind: the contrast limits, the distances in both
 *  modes, or (stable block) the largest between-modes ΔE00 of its samples. */
function groupSummary(group, direction) {
    const kind = directionKind(direction);
    if (kind === "both") {
        return `ΔE00 ${dE(group.max_delta_e_mode_1)} in mode 1 · ${dE(group.max_delta_e_mode_2)} in mode 2`;
    }
    if (kind === "stable") {
        return `ΔE00 between modes ≤ ${dE(group.max_between_delta_e)}`;
    }
    return `ΔE00 ${dE(group.max_close_delta_e)} in ${lowerMode(direction.close_mode)} · ${dE(group.min_far_delta_e)} in ${lowerMode(direction.far_mode)}`;
}

/** One pair (or the stable block): the members' swatches per mode (the
 *  close mode above the far mode for a contrast direction, mode 1 above
 *  mode 2 otherwise), and what it keeps. The stable block lists every
 *  sample's own between-modes ΔE00 under its column. */
function GroupCard({ group, direction, index, entries }) {
    const kind = directionKind(direction);
    const modes = kind === "contrast" ? [direction.close_mode, direction.far_mode] : [1, 2];
    const key = directionKey(direction);
    return (
        <Box
            data-testid="mode-contrast-group"
            sx={{
                border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.25,
                borderTop: `3px solid ${DIRECTION_COLORS[key]}`,
            }}
        >
            <Typography variant="caption" fontWeight={700} color="text.secondary">
                {kind === "stable" ? "Stable samples" : `Pair ${index + 1}`}
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `auto repeat(${group.members.length}, 30px)`,
                    columnGap: 0.5, rowGap: 0.5, alignItems: "center", mt: 0.5,
                }}
            >
                {modes.map((mode) => (
                    <React.Fragment key={mode}>
                        <Typography variant="caption" color="text.secondary" sx={{ pr: 1 }}>
                            {modeLabel(mode)}
                        </Typography>
                        {group.members.map((member) => (
                            <Swatch key={member.sample_id} member={member} mode={mode} entry={entries?.[member.sample_id]} />
                        ))}
                    </React.Fragment>
                ))}
                {kind === "stable" && (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ pr: 1 }}>ΔE00</Typography>
                        {group.members.map((member) => (
                            <Typography
                                key={member.sample_id} variant="caption" color="text.secondary"
                                sx={{ fontSize: 10, textAlign: "center" }} data-testid="mode-contrast-between"
                            >
                                {dE(member.between_delta_e)}
                            </Typography>
                        ))}
                    </>
                )}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                {groupSummary(group, direction)}
            </Typography>
        </Box>
    );
}

/** The text of an empty category: nothing requested, or nothing within the limits. */
function emptyText(direction) {
    if (direction.count === 0) return "Nothing requested.";
    return directionKind(direction) === "stable" ? "No sample within the limit." : "No pair within the limits.";
}

/**
 * The pairs of the Mode Contrast Filter per category, and the stable
 * block. Used by the filter page and by the overview (filter and palette
 * tabs). `entries`: the palette's hover card entries by sample id
 * (overview only).
 */
export default function ModeContrastGroups({ directions, entries = null }) {
    return (
        <Stack spacing={2.5}>
            {directions.map((direction) => {
                const key = directionKey(direction);
                return (
                    <Box key={key} data-testid={`mode-contrast-direction-${key}`}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                            <Typography variant="subtitle2" fontWeight={700}>{directionLabel(direction)}</Typography>
                            <Chip size="small" label={selectedText(direction, direction.groups.length, direction.selected)} />
                        </Stack>
                        {direction.groups.length ? (
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                {direction.groups.map((group, index) => (
                                    <GroupCard key={index} group={group} direction={direction} index={index} entries={entries} />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">{emptyText(direction)}</Typography>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
}
